import LocalizedStrings from "react-localization";
import { useContext, useEffect, useState } from "react";
import { Autocomplete, Flex, Label } from "@aws-amplify/ui-react";
import toast from "react-hot-toast";
import { API, graphqlOperation } from "aws-amplify";

import { LayoutContext } from "layout/context/LayoutContext";
import { globalStrings } from "config/translations/es";
import { listBranches, listCompanies } from "graphql/queries";
import LabelRequired from "components/utils/LabelRequired";

const CompanyBranchFilter = ({
  searchBy,
  setSearchBy,
  onlyCompany,
  withLabels,
  refresh,
}) => {
  const { me } = useContext(LayoutContext);
  const [companies, setCompanies] = useState([]);
  const [branches, setBranches] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [branchesLoading, setBranchesLoading] = useState(false);

  const onChangeSelect = (e) => {
    const { name, value } = e.target;
    if (name === "company") {
      fetchCompanies({ name: value });
    }
    if (name === "branch") fetchBranches({ name: value }, searchBy.company.id);
    setSearchBy((prevSt) => ({
      ...prevSt,
      [name]: {
        id: "",
        display: value,
      },
    }));
  };

  const onSelect = (name, id, display) => {
    setSearchBy((prevSt) => ({
      ...prevSt,
      [name]: {
        display,
        id,
      },
    }));
    if (name === "company") {
      onClearSelect("branch", id);
    }
  };

  const onClearSelect = (name, companyId) => {
    const value = "";
    if (name === "company") {
      fetchCompanies({ name: value });
      onClearSelect("branch", value);
    }
    if (name === "branch") {
      if (companyId) {
        fetchBranches({ name: value }, companyId);
      }
    }
    setSearchBy((prevSt) => ({
      ...prevSt,
      [name]: {
        id: value,
        display: value,
      },
    }));
  };

  async function fetchCompanies({ id = "", name = "" }) {
    let filterObj = {};
    try {
      setCompaniesLoading(true);

      if (me.companyID)
        filterObj = { ...filterObj, or: [{ id: { eq: me.companyID } }] };
      if (me.managedCompanies.length > 0)
        filterObj = {
          ...filterObj,
          or: [
            ...(filterObj?.or ?? []),
            ...me.managedCompanies.map((cp) => ({ id: { eq: cp } })),
          ],
        };

      const companiesData = await API.graphql(
        graphqlOperation(listCompanies, {
          limit:
            !id && !name && Object.keys(filterObj).length <= 0 ? 20 : undefined,
          filter: {
            and: [
              {
                id: { contains: id },
              },
              {
                name: { contains: name },
              },
            ],
            ...filterObj,
          },
        })
      );
      const companiesItems = companiesData.data.listCompanies.items;
      setCompanies(companiesItems);
      setCompaniesLoading(false);
    } catch (err) {
      toast.error(strings.responses.errorFetchCompanies);
    }
  }

  async function fetchBranches({ id = "", name = "" }, companyId) {
    try {
      setBranchesLoading(true);
      const branchesData = await API.graphql(
        graphqlOperation(listBranches, {
          limit: !id && !name ? 20 : undefined,
          filter: {
            companyID: companyId ? { eq: companyId } : undefined,
            and: [
              {
                id: { contains: id },
              },
              {
                name: { contains: name },
              },
            ],
          },
        })
      );
      const branchesItems = branchesData.data.listBranches.items;
      setBranches(branchesItems);
      setBranchesLoading(false);
    } catch (err) {
      toast.error(strings.responses.errorFetchBranches);
    }
  }

  useEffect(() => {
    fetchCompanies({ id: searchBy?.company?.id });
    if (searchBy?.company?.id)
      fetchBranches({ id: searchBy?.branch?.id }, searchBy?.company?.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);

  return (
    <>
      <Flex direction="column" gap="xs">
        {withLabels && (
          <Label>
            <LabelRequired text={strings.fields.company.label} />
          </Label>
        )}
        <Autocomplete
          name="company"
          value={
            companies.find(
              (companyRec) => companyRec.id === searchBy?.company?.id
            )?.name ?? searchBy?.company?.display
          }
          label={strings.fields.company.label}
          placeholder={strings.fields.company.placeholder}
          options={companies.map((companyRec) => ({
            id: companyRec.id,
            label: companyRec.name,
          }))}
          isLoading={companiesLoading}
          onClear={() => onClearSelect("company")}
          onChange={onChangeSelect}
          onSelect={({ id, label }) => onSelect("company", id, label)}
          menuSlots={{ Empty: globalStrings.noResults }}
        ></Autocomplete>
      </Flex>
      {!onlyCompany && (
        <Flex direction="column" gap="xs">
          {withLabels && (
            <Label>
              <LabelRequired text={strings.fields.branch.label} />
            </Label>
          )}
          <Autocomplete
            name="branch"
            value={
              branches.find(
                (branchRec) => branchRec.id === searchBy?.branch?.id
              )?.name ?? searchBy?.branch?.display
            }
            label={strings.fields.branch.label}
            disabled={!searchBy.company.id}
            placeholder={strings.fields.branch.placeholder}
            options={branches.map((branchRec) => ({
              id: branchRec.id,
              label: branchRec.name,
            }))}
            isLoading={branchesLoading}
            onClear={() => onClearSelect("branch", searchBy.company.id)}
            onChange={onChangeSelect}
            onSelect={({ id, label }) => onSelect("branch", id, label)}
            menuSlots={{ Empty: globalStrings.noResults }}
          ></Autocomplete>
        </Flex>
      )}
    </>
  );
};

const strings = new LocalizedStrings({
  es: {
    fields: {
      company: {
        label: "Empresa",
        placeholder: "Buscar empresa...",
      },
      branch: {
        label: "Sede",
        placeholder: "Buscar sede...",
      },
    },
  },
});

export default CompanyBranchFilter;
